import { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { LayoutDashboard, Contact, ListChecks, PhoneCall, Users, BarChart3, Settings as Cog, LogOut, Menu, Bell, Globe, Briefcase, PanelLeftClose, PanelLeftOpen, ChevronRight, X } from "lucide-react";
import crm from "./crmApi";
import { useCrm } from "./CrmApp";
import { LogoMark } from "../components/Logo";
import { Avatar, timeAgo } from "./ui";
import CrmLeads from "./CrmLeads";
import CrmLeadDetail from "./CrmLeadDetail";
import CrmReports from "./CrmReports";
import CrmDashboard from "./CrmDashboard";
import CrmSettings from "./CrmSettings";
import { Tasks } from "./CrmTasks";
import { Team, Calling } from "./CrmTeam";
import WebsiteApp from "./website/WebsiteApp";
import HrApp from "./hr/HrApp";

const NAV = [
  ["Sales", [["", "Dashboard", LayoutDashboard], ["leads", "Leads", Contact], ["tasks", "Follow-ups & Tasks", ListChecks], ["calling", "Calling Activity", PhoneCall]]],
  ["Manage", [["team", "Team Management", Users], ["reports", "Reports", BarChart3]]],
  ["Workspace", [["website", "Website Management", Globe], ["hr", "HRMS & Documents", Briefcase], ["settings", "Settings", Cog]]],
];
const LABEL = Object.fromEntries(NAV.flatMap(([, items]) => items.map(([p, l]) => [p, l])));
export const roleLabel = (r = "") => ({ owner: "Owner", sales_manager: "Sales Manager", bda: "BDA", telecaller: "Telecaller", hr_staff: "HR Staff" }[r] || r.replace("_", " "));

function Sidebar({ collapsed, setCollapsed, mobile, onClose }) {
  const { user, logout } = useCrm();
  const seg = useLocation().pathname.replace(/^\/crm\/?/, "").split("/")[0];
  const allowed = (p) => user.role === "hr_staff" ? p === "hr" : p !== "hr" || user.role === "owner" || !!user.hr_role;
  const mini = collapsed && !mobile;
  return (
    <aside className={`${mini ? "w-16" : "w-60"} h-full bg-[#236B45] text-white flex flex-col transition-[width] duration-200`} data-testid="crm-sidebar">
      <div className={`h-14 flex items-center ${mini ? "justify-center" : "justify-between px-4"} border-b border-white/10 shrink-0`}>
        <Link to="/crm" className="flex items-center gap-2 min-w-0" onClick={onClose}><LogoMark dark className="h-7 w-7 shrink-0" />{!mini && <span className="font-bold text-[15px] whitespace-nowrap">PlotBy<span className="text-[#C9A45C]">Square</span></span>}</Link>
        {mobile && <button onClick={onClose} aria-label="Close menu" className="text-white/70 hover:text-white"><X size={18} /></button>}
      </div>
      <nav className="flex-1 overflow-y-auto py-3 no-scrollbar">
        {NAV.map(([group, items]) => {
          const vis = items.filter(([p]) => allowed(p));
          if (!vis.length) return null;
          return (
            <div key={group} className="mb-3">
              {!mini && <p className="px-5 pb-1 text-[10.5px] font-semibold tracking-wider uppercase text-white/40">{group}</p>}
              {vis.map(([path, label, Icon]) => (
                <Link key={label} to={`/crm/${path}`} onClick={onClose} title={mini ? label : undefined} data-testid={`crm-nav-${label.toLowerCase().split(" ")[0]}`}
                  className={`crm-nav-item ${seg === path ? "active" : ""} ${mini ? "justify-center !px-0" : ""}`}>
                  <Icon size={17} className="shrink-0" />{!mini && <span className="truncate">{label}</span>}
                </Link>
              ))}
            </div>
          );
        })}
      </nav>
      <div className="border-t border-white/10 py-2 shrink-0">
        {!mobile && <button onClick={() => setCollapsed(!collapsed)} data-testid="crm-sidebar-toggle" title={collapsed ? "Expand sidebar" : "Collapse sidebar"} className={`crm-nav-item w-[calc(100%-16px)] ${mini ? "justify-center !px-0" : ""}`}>{collapsed ? <PanelLeftOpen size={17} /> : <><PanelLeftClose size={17} /> <span>Collapse</span></>}</button>}
        <button onClick={logout} data-testid="crm-logout" title={mini ? "Logout" : undefined} className={`crm-nav-item w-[calc(100%-16px)] ${mini ? "justify-center !px-0" : ""}`}><LogOut size={17} />{!mini && <span>Logout</span>}</button>
      </div>
    </aside>
  );
}

function Notifications() {
  const nav = useNavigate();
  const loc = useLocation();
  const [notif, setNotif] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  useEffect(() => { crm.notifications().then(setNotif).catch(() => {}); }, [loc.pathname]);
  const readAll = () => crm.readNotifications().then(() => setNotif((n) => ({ ...n, unread: 0, items: n.items.map((i) => ({ ...i, read: true })) })));
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} aria-label="Notifications" className="crm-btn crm-btn-ghost crm-btn-icon relative z-40" data-testid="crm-notif">
        <Bell size={18} />{notif.unread > 0 && <span className="absolute top-0.5 right-0.5 bg-[#DC2626] text-white text-[9.5px] font-bold min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center" data-testid="notif-count">{notif.unread > 99 ? "99+" : notif.unread}</span>}
      </button>
      {open && <>
        <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
        <div className="absolute right-0 top-10 w-80 max-w-[90vw] bg-white border border-[#E4EBE4] rounded-xl pbs-shadow z-40 crm-fade" data-testid="notif-panel">
          <div className="flex items-center justify-between px-4 h-11 border-b border-[#E4EBE4]">
            <span className="text-sm font-semibold">Notifications</span>
            {notif.unread > 0 && <button data-testid="notif-read-all" onClick={readAll} className="text-xs font-semibold text-[#236B45] hover:underline">Mark all read</button>}
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-[#E4EBE4]">
            {notif.items.length === 0 ? <p className="p-5 text-sm text-[#5F6E64] text-center">You're all caught up.</p> : notif.items.map((n) => (
              <button key={n.id} onClick={() => { setOpen(false); if (n.lead_id) nav(`/crm/leads/${n.lead_id}`); }} className={`w-full text-left px-4 py-2.5 hover:bg-[#F7FAF7] transition-colors flex gap-2.5 ${!n.read ? "bg-[#F0F7F2]/60" : ""}`}>
                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-[#236B45]"}`} />
                <span className="min-w-0"><span className="block text-[13px] text-[#25352C]">{n.text}</span><span className="block text-[11px] text-[#5F6E64] mt-0.5">{timeAgo(n.at)}</span></span>
              </button>
            ))}
          </div>
        </div>
      </>}
    </div>
  );
}

function ProfileMenu() {
  const { user, logout } = useCrm();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} className="relative z-40 flex items-center gap-2 rounded-lg pl-1 pr-2 h-9 hover:bg-[#F0F5F0] transition-colors" data-testid="crm-profile-menu">
        <Avatar name={user.name} size={28} />
        <span className="hidden sm:block text-left leading-tight"><span className="block text-[13px] font-semibold">{user.name}</span><span className="block text-[11px] text-[#5F6E64]">{roleLabel(user.role)}</span></span>
      </button>
      {open && <>
        <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
        <div className="absolute right-0 top-11 w-60 bg-white border border-[#E4EBE4] rounded-xl pbs-shadow z-40 py-1.5 crm-fade" data-testid="profile-panel">
          <div className="px-4 py-2 border-b border-[#E4EBE4] mb-1"><p className="text-sm font-semibold truncate">{user.name}</p><p className="text-xs text-[#5F6E64] truncate">{user.email}</p><p className="text-[11px] text-[#236B45] font-semibold mt-1">{roleLabel(user.role)}{user.hr_role ? ` · HR: ${user.hr_role.replace("_", " ")}` : ""}</p></div>
          <button onClick={logout} data-testid="profile-logout" className="w-full flex items-center gap-2 px-4 h-9 text-sm text-[#B91C1C] hover:bg-[#FEF2F2]"><LogOut size={15} /> Log out</button>
        </div>
      </>}
    </div>
  );
}

function Breadcrumbs() {
  const parts = useLocation().pathname.replace(/^\/crm\/?/, "").split("/").filter(Boolean);
  const top = LABEL[parts[0] || ""] || "Dashboard";
  const sub = parts[0] === "leads" && parts[1] ? "Lead details" : null;
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[13px] min-w-0" data-testid="crm-breadcrumbs">
      <span className="text-[#5F6E64] hidden sm:inline">CRM</span><ChevronRight size={14} className="text-[#93A69A] hidden sm:inline" />
      {sub ? <><Link to={`/crm/${parts[0]}`} className="text-[#5F6E64] hover:text-[#25352C]">{top}</Link><ChevronRight size={14} className="text-[#93A69A]" /><span className="font-semibold truncate">{sub}</span></>
        : <span className="font-semibold truncate" data-testid="crm-page-title">{top}</span>}
    </nav>
  );
}

export default function Shell() {
  const { user } = useCrm();
  const loc = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("crm_sidebar") === "1");
  const [drawer, setDrawer] = useState(false);
  useEffect(() => { localStorage.setItem("crm_sidebar", collapsed ? "1" : "0"); }, [collapsed]);
  useEffect(() => { setDrawer(false); }, [loc.pathname]);
  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block sticky top-0 h-screen shrink-0"><Sidebar collapsed={collapsed} setCollapsed={setCollapsed} /></div>
      {drawer && <div className="lg:hidden fixed inset-0 z-50 flex"><div className="absolute inset-0 bg-black/50" onClick={() => setDrawer(false)} /><div className="relative h-full crm-fade"><Sidebar mobile onClose={() => setDrawer(false)} /></div></div>}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 bg-white/95 backdrop-blur border-b border-[#E4EBE4] flex items-center justify-between gap-3 px-4 sm:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-2 min-w-0">
            <div className="lg:hidden"><button onClick={() => setDrawer(true)} aria-label="Open menu" className="crm-btn crm-btn-ghost crm-btn-icon" data-testid="crm-menu-btn"><Menu size={18} /></button></div>
            <Breadcrumbs />
          </div>
          <div className="flex items-center gap-1.5"><Notifications /><ProfileMenu /></div>
        </header>
        <main className="flex-1 w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 overflow-x-hidden">
          <Routes>
            <Route path="/" element={user.role === "hr_staff" ? <Navigate to="/crm/hr" /> : <CrmDashboard />} />
            <Route path="leads" element={<CrmLeads />} />
            <Route path="leads/:id" element={<CrmLeadDetail />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="calling" element={<Calling />} />
            <Route path="team" element={<Team />} />
            <Route path="reports" element={<CrmReports />} />
            <Route path="website/*" element={<WebsiteApp />} />
            <Route path="hr/*" element={<HrApp />} />
            <Route path="settings" element={<CrmSettings />} />
            <Route path="*" element={<CrmDashboard />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
